import {
  TonProofItemReplySuccess,
  useTonAddress,
  useTonConnectUI,
} from '@tonconnect/ui-react';
import { AxiosError } from 'axios';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AccountInterface, constants } from 'starknet';
import {
  useAccount as useEVMAccount,
  useDisconnect as useEVMDisconnect,
  useSignMessage,
} from 'wagmi';

import { useStarknetAccount } from '@/starknet/hooks/account';
import {
  useNetwork,
  useConnect as useStarknetConnect,
  useDisconnect as useStarknetDisconnect,
} from '@starknet-react/core';
import { Address } from '@ton/core';
import { Connector } from 'starknetkit';
import { toHex } from 'viem';
import { useNotify } from '../hooks/useToast';
import { api } from '../services/api';
import { WrongAccountChainIdError } from '../starknet/errors';
import { AppTypedData } from '../starknet/types/app_typed_data';
import { getAppStarknetChanId } from '../starknet/utils/getAppStarknetChainId';
import { useAppStore } from '../stores/app.store';
import { useSignStore } from '../stores/sign.store';
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react';
import base58 from 'bs58';
import { useEagerStarknetConnect } from '@/hooks/useEagerStarknetConnect';
import { useWallet as useTronWallet } from '@tronweb3/tronwallet-adapter-react-hooks';
import { useFuelWallet } from '@/fuel/hooks/useFuelWallet';
import { useBitcoinWallet } from '@/bitcoin/hooks/useBitcoinWallet';
import { useSuiWallet } from '@/sui/hooks/useSuiWallet';

// import useAppStore from '../../stores/appStore';
interface PropsTypes {
  children: React.ReactNode;
}
export enum NetworkTypes {
  EVM = 'EVM',
  ZK_SYNC_ERA = 'ZK_SYNC_ERA',
  STARKNET = 'STARKNET',
  TON = 'TON',
  SOLANA = 'SOLANA',
  TRON = 'TRON',
  FUEL = 'FUEL',
  BITCOIN = 'BTC',
  SUI = 'SUI',
}

const starknetNetworks: Record<string, string> = {
  [constants.StarknetChainId.SN_SEPOLIA]: 'Sepolia Testnet',
  [constants.StarknetChainId.SN_MAIN]: 'MainNet',
};

export const routesWithSign = [
  'app.',
  '/send',
  '/phases/',
  '/quests',
  '/adventure',
  '/adventures',
];

type IOverloadedActivateFunction = {
  (networkType: NetworkTypes.STARKNET, connector: Connector): Promise<void>;
};
export type ContextType = {
  handleDeactivate: (networkType: NetworkTypes) => void;
  handleActivate: IOverloadedActivateFunction;
};

const InitialContextValues = {
  handleActivate: async () => {},
  handleDeactivate: () => {},
};

export const WalletConnectInteractorContext =
  createContext<ContextType>(InitialContextValues);
export const useWalletConnectInteractor = () =>
  useContext(WalletConnectInteractorContext);

export const WalletConnectInteractorProvider = ({ children }: PropsTypes) => {
  const { address: evmAddress } = useEVMAccount();
  const { disconnect } = useEVMDisconnect();
  const {
    signMessage,
    data,
    signMessageAsync: signEvmMessage,
  } = useSignMessage();
  const { address: starknetAddress, account: starknetAccount } =
    useStarknetAccount();
  const { connectAsync: connectStarknet } = useStarknetConnect();
  const { disconnect: disconnectStarknet } = useStarknetDisconnect();
  const {
    wallet: solanaWallet,
    signMessage: signSolanaMessage,
    publicKey: solanaAddress,
  } = useSolanaWallet();

  const { address: fuelAddress, signMessage: signFuelMessage } =
    useFuelWallet();

  const { address: bitcoinAddress, signMessage: signBitcoinMessage } =
    useBitcoinWallet();

  const {
    address: tronAddress,
    signMessage: signTronMessage,
    disconnect: disconnectTron,
  } = useTronWallet();

  const {
    address: suiAddress,
    signMessage: singSuiMessage,
    disconnect: disconnectSui,
  } = useSuiWallet();

  const navigate = useNavigate();
  const [networkFrom] = useAppStore(s => [s.networkFrom]);
  const { resign, setResign } = useSignStore();

  const { notify } = useNotify();
  const { chain } = useNetwork();

  const location = useLocation();

  useEagerStarknetConnect();

  const checkIsAuthWallet = async (
    address: string,
    network_type: NetworkTypes
  ) => {
    try {
      await api.checkWallet(address, network_type);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleActivate = useCallback<IOverloadedActivateFunction>(
    async (networkType, connector) => {
      try {
        if (networkType === NetworkTypes.STARKNET && connector) {
          connectStarknet({ connector })
            .then(() => {
              window.sessionStorage.setItem('starknet-provider', connector.id);
            })
            .catch((err: any) => {
              if (err instanceof WrongAccountChainIdError) {
                notify({
                  title: 'Wrong StarkNet Network',
                  meassage: `Please ensure to switch StarkNet network to ${
                    starknetNetworks[getAppStarknetChanId()]
                  } in your wallet`,
                  type: 'error',
                });
                return;
              }
              console.error(err);
            });

          return;
        }
      } catch (e) {
        console.error(e);
      }
    },
    [evmAddress, connectStarknet, networkFrom]
  );

  const deactivateEthWallet = useCallback(() => {
    window.sessionStorage.removeItem('provider');
    disconnect();
  }, [disconnect]);

  const deactivateStarknetWallet = useCallback(() => {
    window.sessionStorage.removeItem('starknet-provider');
    disconnectStarknet();
  }, [disconnectStarknet]);

  const deactivateSolanaWallet = useCallback(() => {
    solanaWallet?.adapter.disconnect();
  }, [solanaWallet]);

  const deactivateTronWallet = useCallback(async () => {
    await disconnectTron();
  }, [disconnectTron]);

  const handleDeactivate = (networkType: NetworkTypes) => {
    if (
      location.pathname !== '/' &&
      location.pathname !== '/explorer' &&
      !location.pathname.includes('/phases') &&
      !location.pathname.includes('/quests')
    ) {
      navigate('/send');
    }

    if (networkType === NetworkTypes.STARKNET) {
      deactivateStarknetWallet();
    }
  };

  const deactivateWrongStarknetNetwork = useCallback(async () => {
    if (starknetAddress && chain && networkFrom) {
      if (
        networkFrom.network_type === NetworkTypes.STARKNET &&
        toHex(chain?.id) !== networkFrom.chainId
      ) {
        handleDeactivate(NetworkTypes.STARKNET);
        const network = starknetNetworks[networkFrom.chainId];
        notify({
          title: 'Wrong StarkNet Network',
          meassage: `Please ensure to switch StarkNet network to ${network} in your wallet`,
          type: 'error',
        });
      }
    }
  }, [chain, starknetAddress, networkFrom]);

  const value = { handleActivate, handleDeactivate };

  const [tonconnect] = useTonConnectUI();
  const tonAddress = useTonAddress();
  const oldTonAddressRef = useRef(tonAddress);

  const errorNotifier = useCallback(
    (error?: unknown) => {
      if (error instanceof AxiosError) {
        return notify({
          type: 'error',
          meassage: error.response?.data.message,
        });
      }
      return notify({
        type: 'error',
        meassage: 'Something went wrong. Please try again later.',
      });
    },
    [notify]
  );

  const getTypedDataWithAddress = async (
    address: string
  ): Promise<AppTypedData> => {
    const { data } = await api.getSignMessage();

    data.message.value = `${data.message.value}\n${address}`;
    return data;
  };

  const signStarknetWallet = useCallback(
    async (account: AccountInterface, address: string) => {
      const isAuth = await checkIsAuthWallet(address, NetworkTypes.STARKNET);
      if (isAuth) return;

      try {
        const typedData = await getTypedDataWithAddress(address);
        typedData.domain.chainId = getAppStarknetChanId();
        const signed = await account.signMessage(typedData);
        await api.validateSignedMessage(address, NetworkTypes.STARKNET, signed);
      } catch (error) {
        errorNotifier(error);
        deactivateStarknetWallet();
      }
    },
    [deactivateStarknetWallet, errorNotifier]
  );

  const signEthWalletHelper = useCallback(
    async (address: string) => {
      try {
        const typedData = await getTypedDataWithAddress(address);
        await signEvmMessage({ message: typedData.message.value });
      } catch (error) {
        deactivateEthWallet();
        errorNotifier(error);
      }
    },
    [deactivateEthWallet, errorNotifier, signEvmMessage]
  );

  useEffect(() => {
    if (data && evmAddress) {
      api.validateSignedMessage(evmAddress, NetworkTypes.EVM, data);
    }
  }, [data, evmAddress]);

  const signEthWallet = useCallback(
    async (address?: `0x${string}`) => {
      if (!address) return;
      const isAuth = await checkIsAuthWallet(address, NetworkTypes.EVM);
      if (isAuth) return;

      await signEthWalletHelper(address);
    },
    [signEthWalletHelper]
  );

  const signSolWallet = useCallback(
    async (
      address: string,
      signSolMessage: (message: Uint8Array) => Promise<Uint8Array>
    ) => {
      try {
        if (!address) return;

        const isAuth = await checkIsAuthWallet(address, NetworkTypes.SOLANA);
        if (isAuth) return;

        const typedData = await getTypedDataWithAddress(address);
        const encodedMessage = new TextEncoder().encode(
          typedData.message.value
        );
        const signedMessage = await signSolMessage(encodedMessage);
        const signature = base58.encode(signedMessage as Uint8Array);
        await api.validateSignedMessage(
          address,
          NetworkTypes.SOLANA,
          signature
        );
      } catch (error) {
        console.error(error);
        deactivateSolanaWallet();
        errorNotifier(error);
      }
    },
    [deactivateSolanaWallet, errorNotifier]
  );

  const signTronWallet = useCallback(
    async (
      address: string,
      signTronMessage: (message: string) => Promise<string>
    ) => {
      try {
        if (!address) return;

        const isAuth = await checkIsAuthWallet(address, NetworkTypes.TRON);
        if (isAuth) return;

        const typedData = await getTypedDataWithAddress(address);
        const signature = await signTronMessage(typedData.message.value);
        await api.validateSignedMessage(address, NetworkTypes.TRON, signature);
      } catch (error) {
        console.error(error);
        deactivateTronWallet();
        errorNotifier(error);
      }
    },
    [deactivateTronWallet, errorNotifier]
  );

  const signFuelWallet = useCallback(
    async (
      address: string,
      signFuelMessage: (message: string) => Promise<string>
    ) => {
      try {
        if (!address) return;

        const isAuth = await checkIsAuthWallet(address, NetworkTypes.FUEL);
        if (isAuth) return;

        const typedData = await getTypedDataWithAddress(address);
        const signedMessage = await signFuelMessage(typedData.message.value);
        await api.validateSignedMessage(
          address,
          NetworkTypes.FUEL,
          signedMessage
        );
      } catch (error) {
        console.error(error);
        errorNotifier(error);
      }
    },
    [errorNotifier]
  );

  const signSuiWallet = useCallback(
    async (
      address: string,
      signSuiMessage: (message: string) => Promise<string>
    ) => {
      try {
        if (!address) return;

        const isAuth = await checkIsAuthWallet(address, NetworkTypes.SUI);
        if (isAuth) return;

        const typedData = await getTypedDataWithAddress(address);
        const signedMessage = await signSuiMessage(typedData.message.value);
        await api.validateSignedMessage(
          address,
          NetworkTypes.SUI,
          signedMessage
        );
      } catch (error) {
        console.error(error);
        await disconnectSui();
        errorNotifier(error);
      }
    },
    [disconnectSui, errorNotifier]
  );

  const signBitcoinWallet = useCallback(
    async (
      address: string,
      signBitcoinMessage: (message: string) => Promise<string>
    ) => {
      try {
        if (!address) return;

        const isAuth = await checkIsAuthWallet(address, NetworkTypes.BITCOIN);
        if (isAuth) return;

        const typedData = await getTypedDataWithAddress(address);
        const signedMessage = await signBitcoinMessage(typedData.message.value);
        await api.validateSignedMessage(
          address,
          NetworkTypes.BITCOIN,
          signedMessage
        );
      } catch (error) {
        console.error(error);
        errorNotifier(error);
      }
    },
    [errorNotifier]
  );

  const signTonWallet = useCallback(
    async (
      address: string,
      tonProof: TonProofItemReplySuccess & { stateInit: string }
    ) => {
      try {
        if (!address) return;
        oldTonAddressRef.current = address;
        const isAuth = await checkIsAuthWallet(address, NetworkTypes.TON);
        if (isAuth) return;

        await api.validateSignedMessage(
          Address.parse(address).toRawString(),
          NetworkTypes.TON,
          JSON.stringify({ ...tonProof.proof, stateInit: tonProof.stateInit })
        );
      } catch (error) {
        errorNotifier(error);
        await tonconnect.connector.disconnect();
      }
    },
    [tonconnect, errorNotifier]
  );

  const generateTonProof = useCallback(async () => {
    tonconnect.setConnectRequestParameters({ state: 'loading' });
    const { data } = await api.getSignMessage();
    if (data) {
      tonconnect.setConnectRequestParameters({
        state: 'ready',
        value: { tonProof: btoa(data.message.value) },
      });
    } else {
      tonconnect.setConnectRequestParameters(null);
    }
  }, [tonconnect]);

  const resignWalletHelper = useCallback(
    async (type: NetworkTypes) => {
      setResign(null);
      if (
        type === NetworkTypes.STARKNET &&
        starknetAddress &&
        starknetAccount
      ) {
        return await signStarknetWallet(starknetAccount, starknetAddress);
      } else if (
        type === NetworkTypes.TON &&
        tonAddress &&
        tonconnect.wallet?.connectItems?.tonProof &&
        'proof' in tonconnect.wallet?.connectItems?.tonProof
      ) {
        return await signTonWallet(tonAddress, {
          ...tonconnect.wallet?.connectItems?.tonProof,
          stateInit: tonconnect.wallet.account.walletStateInit,
        });
      } else if (
        type === NetworkTypes.SOLANA &&
        solanaAddress &&
        signSolanaMessage
      ) {
        return await signSolWallet(solanaAddress.toBase58(), signSolanaMessage);
      } else if (type === NetworkTypes.TRON && tronAddress && signTronMessage) {
        return await signTronWallet(tronAddress, signTronMessage);
      } else if (type === NetworkTypes.FUEL && fuelAddress) {
        return await signFuelWallet(fuelAddress, signFuelMessage);
      } else if (type === NetworkTypes.BITCOIN && bitcoinAddress) {
        return await signBitcoinWallet(bitcoinAddress, signBitcoinMessage);
      } else if (type === NetworkTypes.SUI && suiAddress) {
        return await signSuiWallet(suiAddress, singSuiMessage);
      } else if (evmAddress) {
        return await signEthWalletHelper(evmAddress);
      }
    },
    [
      setResign,
      evmAddress,
      starknetAccount,
      starknetAddress,
      tonAddress,
      solanaAddress,
      tronAddress,
      fuelAddress,
      bitcoinAddress,
      suiAddress,
      signFuelMessage,
      signFuelWallet,
      signBitcoinMessage,
      signBitcoinWallet,
      signSolWallet,
      signSolanaMessage,
      signStarknetWallet,
      signTonWallet,
      signTronMessage,
      signTronWallet,
      signSuiWallet,
      singSuiMessage,
      tonconnect.wallet?.connectItems?.tonProof,
      tonconnect.wallet?.account.walletStateInit,
      signEthWalletHelper,
    ]
  );

  useEffect(() => {
    if (
      evmAddress &&
      routesWithSign.some(route => window.location.href.includes(route))
    ) {
      signEthWallet(evmAddress);
    }
  }, [evmAddress]);

  useEffect(() => {
    if (
      solanaAddress &&
      signSolanaMessage &&
      routesWithSign.some(route => window.location.href.includes(route))
    ) {
      signSolWallet(solanaAddress.toBase58(), signSolanaMessage);
    }
  }, [solanaAddress]);

  useEffect(() => {
    if (
      tronAddress &&
      signTronMessage &&
      routesWithSign.some(route => window.location.href.includes(route))
    ) {
      signTronWallet(tronAddress, signTronMessage);
    }
  }, [tronAddress]);

  useEffect(() => {
    if (
      fuelAddress &&
      routesWithSign.some(route => window.location.href.includes(route))
    ) {
      signFuelWallet(fuelAddress, signFuelMessage);
    }
  }, [fuelAddress]);

  useEffect(() => {
    if (
      bitcoinAddress &&
      routesWithSign.some(route => window.location.href.includes(route))
    ) {
      signBitcoinWallet(bitcoinAddress, signBitcoinMessage);
    }
  }, [bitcoinAddress]);

  useEffect(() => {
    if (
      suiAddress &&
      routesWithSign.some(route => window.location.href.includes(route))
    ) {
      signSuiWallet(suiAddress, singSuiMessage);
    }
  }, [suiAddress]);

  useEffect(() => {
    if (
      starknetAccount &&
      starknetAddress &&
      routesWithSign.some(route => window.location.href.includes(route))
    ) {
      signStarknetWallet(starknetAccount, starknetAddress);
    }
  }, [starknetAccount, starknetAddress, networkFrom, signStarknetWallet]);

  useEffect(() => {
    if (routesWithSign.some(route => window.location.href.includes(route))) {
      generateTonProof();
      if (
        tonAddress &&
        tonconnect.wallet?.connectItems?.tonProof &&
        'proof' in tonconnect.wallet?.connectItems?.tonProof
      ) {
        signTonWallet(tonAddress, {
          ...tonconnect.wallet?.connectItems?.tonProof,
          stateInit: tonconnect.wallet.account.walletStateInit,
        });
      }
    }
  }, [
    tonAddress,
    generateTonProof,
    tonconnect.wallet?.connectItems?.tonProof,
    tonconnect.wallet?.account.walletStateInit,
    signTonWallet,
  ]);

  useEffect(() => {
    deactivateWrongStarknetNetwork();
  }, [deactivateWrongStarknetNetwork]);

  useEffect(() => {
    if (resign) {
      resignWalletHelper(resign);
    }
  }, [resign, resignWalletHelper]);

  return (
    <WalletConnectInteractorContext.Provider value={value}>
      {children}
    </WalletConnectInteractorContext.Provider>
  );
};
